import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitHandler, useForm } from "react-hook-form";
import { cpaInfoSchema } from "../../yup";
import { useUpdateCpaMutation } from "../../services/api";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../store/store";
import { updatePurlUser } from "../../store/reducers/purlUserReducer";
import { IoIosCloseCircle } from "react-icons/io";
import Loader from "../Loader";
import { tracker } from "../../services/mixpanel";

interface CpaFieldValues {
  cpaName: string;
  cpaEmail: string;
  cpaPhone: string;
}

interface CpaFormProps {
  isOpen: boolean;
  onClose: () => void;
  initialValues?: Partial<CpaFieldValues>;
}

export const CpaForm = ({ isOpen, onClose, initialValues }: CpaFormProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CpaFieldValues>({
    resolver: yupResolver(cpaInfoSchema),
    defaultValues: initialValues,
    mode: "onChange",
  });

  const [updateCpa, { isLoading: cpaLoading }] = useUpdateCpaMutation();
  const loginData = useSelector((state: RootState) => state.login);
  const dispatch = useAppDispatch();

  const submitCpa: SubmitHandler<CpaFieldValues> = async (values) => {
    tracker.dashboard.cpa.formSubmit(!!initialValues?.cpaEmail);
    try {
      const sendData = {
        crm_Contact_ID: loginData.zohoCustomerId,
        cpaName: values.cpaName,
        cpaEmail: values.cpaEmail,
        cpaPhone: values.cpaPhone,
      };

      const result = await updateCpa(sendData).unwrap();
      if (result.data.code !== "success") throw new Error(result?.message);

      dispatch(
        updatePurlUser({
          cpaName: values.cpaName,
          cpaEmail: values.cpaEmail,
          cpaPhone: values.cpaPhone,
        })
      );

      onClose();
    } catch (err) {
      console.log("err in updating cpa", err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="w-full h-full fixed flex justify-center items-center bg-opacity-40 bg-black z-10">
      <form
        onSubmit={handleSubmit(submitCpa)}
        className="w-1/3 min-w-[300px] bg-[#CFD8E4] p-8 rounded-xl text-center relative"
      >
        <IoIosCloseCircle
          onClick={onClose}
          className="absolute right-2 top-2 text-2xl cursor-pointer"
        />
        <div className="flex flex-col w-full mb-4">
          <label className="text-black font-semibold text-start">
            CPA Name
          </label>
          <input
            {...register("cpaName")}
            className="p-2 text-[17px] rounded-md w-full"
          />
          {errors.cpaName && (
            <p className="text-red-500 text-start text-sm mt-1">
              {errors.cpaName.message}
            </p>
          )}
        </div>
        <div className="flex flex-col w-full mb-4">
          <label className="text-black font-semibold text-start">
            CPA Email
          </label>
          <input
            {...register("cpaEmail")}
            className="p-2 text-[17px] rounded-md w-full"
          />
          {errors.cpaEmail && (
            <p className="text-red-500 text-start text-sm mt-1">
              {errors.cpaEmail.message}
            </p>
          )}
        </div>
        <div className="flex flex-col w-full mb-4">
          <label className="text-black font-semibold text-start">
            CPA Phone Number
          </label>
          <input
            {...register("cpaPhone")}
            className="p-2 text-[17px] rounded-md w-full"
          />
          {errors.cpaPhone && (
            <p className="text-red-500 text-start text-sm mt-1">
              {errors.cpaPhone.message}
            </p>
          )}
        </div>
        <button className="h-[32px] px-6 rounded-sm bg-[#356C58] text-white">
          {cpaLoading ? <Loader isLoading={true} size={20} /> : "SUBMIT"}
        </button>
      </form>
    </div>
  );
};
